// An option to view previous and next row data should be available, need to code
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import {
  styles,
  expandedViewStyles,
  subsheetStyles,
  expandedSubsheetStyles,
  keyframes
} from './RowDetailPanelStyles';
import {
  Box,
  IconButton,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  Paper,
  Popover,
  Typography
} from '@material-ui/core';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import SettingsIcon from "@material-ui/icons/Settings";
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import Autocomplete from '@material-ui/lab/Autocomplete';
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import ApiClient from "utils/ApiClient";
import { formatDate, formatTime } from "../../helpers/dateFormatter";
import { normalizeColOptions } from "app/websocketManager/utils";
import tinycolor from "tinycolor2";
import { Tooltip } from "@material-ui/core";
import COLORS from "scss/colors.module.scss";
import { renderTextValue } from "../MultiLine/MultiLineColumnRenderer";
import { getCheckBox } from "../Boolean/BooleanColumnRenderer";
import FileViewerForRowDetailPanel from "../FileOrAttachment/FileViewerForRowDetailPanel";
import { getFormattedCommentsArray } from "../CommentsIdColumnRenderer/CommentsIdColumnRenderer";
import { getFormattedValue } from "../Number/NumberColumnRenderer";
import { checkEditability } from "./Validations";
import { getEditorByColumnType, parseFiles, getColumnDefById } from "../ReusableColumnEditorsFactory/ColumnEditorFactory";

class LRUCache {
  constructor(limit = 50) {
    this.limit = limit;
    this.map = new Map();
  }

  get(key) {
    if (!this.map.has(key)) return null;
    const value = this.map.get(key);
    this.map.delete(key);
    this.map.set(key, value);
    return value;
  }

  set(key, value) {
    if (this.map.has(key)) {
      this.map.delete(key);
    } else if (this.map.size >= this.limit) {
      const firstKey = this.map.keys().next().value;
      this.map.delete(firstKey);
    }
    this.map.set(key, value);
  }

  has(key) {
    return this.map.has(key);
  }

  delete(key) {
    return this.map.delete(key);
  }

  clear() {
    this.map.clear();
  }
}

const rowCache = new LRUCache(50);             // caches full formatted row for row_id
// const columnFormatCache = new LRUCache(1500);  // caches column.format(columnId,value) -> formatted result
const subsheetCache = new LRUCache(50);        // caches subsheet formatted arrays by key parentRowId::subsheetListId

const RowDetailPanel = ({ params, data, onClose, columns }) => {
  const store = params?.store ?? window.appStore?.containerStore?.currentViewStore;
  const [currentRow, setCurrentRow] = useState(data);
  const currentRowRef = useRef(currentRow);
  const [formattedData, setFormattedData] = useState({});
  const [metaData, setMetaData] = useState({});
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isHeaderSaving, setIsHeaderSaving] = useState(false);
  const [options, setOptions] = useState([]);
  const [dataIncludingSubsheets, setDataIncludingSubsheets] = useState({...currentRow});
  const [recordIndex, setRecordIndex] = useState(getCurrentRowIndex());
  // const rowNode = store?.gridApi?.getRowNode(currentRow?.uniqueId ?? currentRow?.row_id);
  const isAdmin = store?.containerStore?.permissions?.isAdmin() ?? false;
  // const columns = store?.listConfig?.listData;
  // const formulaAndCodeSnippetColumnIds = {};

  // const columnInfoByName = formatColumnInfoByName() ?? {};
  const [editorState, setEditorState] = useState({ open: false, editorColKey: null, editorValue: "" });

  const settingsRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    currentRowRef.current = currentRow;
  }, [currentRow]);

  const handleEditorState = (open, value = null, colKey = null) => {
    if (colKey === null && value === null) {
      setEditorState((prev) => ({...prev, open}));
    } else {
      setEditorState({
        open,
        editorColKey: colKey,
        editorValue: value
      });
    }
    store.isRowViewEditorOpen = open;
  };

  const normalizedColumnsById = useMemo(() => {
    const out = new Map();
    (columns || []).forEach(c => {
      out.set(c.id, { ...c, normalizedColumnOptions: normalizeColOptions(c?.options) });
    });
    return out;
  }, [columns]);

  // Build column info (by name) and map of formula/code columns (stable per open row set)
  const { columnInfoByName, formulaAndCodeSnippetColumnIds } = useMemo(() => {
    const info = {};
    const formulaIds = {};
    const cols = columns ?? [];
    cols.forEach(column => {
      if ((column.id > 0 || ['created_at', 'updated_at'].includes(column?.id)) && column.name.trim() !== '') {
        const normalized = normalizedColumnsById.get(column.id) || column;
        info[column.name] = {
          ...normalized,
          normalizedColumnOptions: normalized.normalizedColumnOptions ?? normalizeColOptions(normalized?.options)
        };
        if (column.column_type_id === '7' || column.column_type_id === '19') {
          formulaIds[column.id] = info[column.name];
        }
      }
    });
    const rowRef = currentRow;
    if (rowRef?.comments && rowRef?.comments.length > 0) {
      info[`Notes/Comments (${rowRef.comments.length})`] =
        (store?.columns || []).find(col => col.id === 'comments_id') || {};
    }
    return { columnInfoByName: info, formulaAndCodeSnippetColumnIds: formulaIds };
  }, [columns, normalizedColumnsById, store?.columns, currentRow.comments]);

  const getFormattedHeaderName = useCallback((row) => {
    try {
      let headerName = store?.view?.options?.rowViewHeader ?? '';
      if (headerName !== '') {
        const isColumnId = row?.hasOwnProperty('column_' + headerName);
        if (isColumnId) return row?.['column_' + headerName] || '';
        return headerName;
      }
      return '';
    } catch {
      return '';
    }
  }, [store?.view?.options?.rowViewHeader]);

  const [headerTitle, setHeaderTitle] = useState(
    () => getFormattedHeaderName(currentRowRef.current) || `Row ${currentRowRef.current?.row_id}`
  );

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (store) {
        // store.rowDetailScrollPos = el.scrollTop ? el.scrollTop : store.rowDetailScrollPos;
        store.rowDetailScrollPos = el?.scrollTop
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => {
      el.removeEventListener("scroll", handleScroll);
      rowCache.clear();
      subsheetCache.clear();
      // columnFormatCache.clear()
    }
  }, [store]);

  useLayoutEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = store?.rowDetailScrollPos ?? 0;
    }
  });

  useEffect(() => {
    const rowRef = currentRowRef.current;
    setHeaderTitle(getFormattedHeaderName(rowRef) || `Row ${rowRef?.row_id}`);
  }, [currentRow?.row_id, getFormattedHeaderName, currentRow]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        settingsRef.current &&
        !settingsRef.current.contains(event.target) &&
        settingsButtonRef.current &&
        !settingsButtonRef.current.contains(event.target)
      ) {
        setSettingsOpen(false);
      }
    };

    if (settingsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [settingsOpen]);

  useEffect(() => {
    const createInitialOptions = () => {
      const cols = store?.columns || [];
      const columnIdsByName = cols.reduce((acc, column) => {
        if (
          column.id > 0 &&
          column.name.trim() !== '' &&
          column.name.trim().toLowerCase() !== 'star' &&
          column.alias_name !== 'createdBy' &&
          column.column_type_id != '6' &&
          column.column_type_id != '9'
        ) {
          acc[column.name] = column.id;
        }
        return acc;
      }, {});
      store.columnIdsByName = columnIdsByName;
      return Object.keys(columnIdsByName).filter(v => v !== '' && v !== headerTitle);
    };

    const initOptions = createInitialOptions();
    setOptions(initOptions);
  }, [store, headerTitle]);

  function getNewRows() {
    return (store.newRows || []).filter(row => row.row_id > 0);
  }

  // function getCurrentRowIndex() {
  //   const rowRef = currentRowRef.current;
  //   const newRows = getNewRows();
  //   return [...store?.allRowsData, ...newRows]?.findIndex(row => row.row_id === rowRef?.row_id) ?? 0;
  // }

  // function getTotalRowsCount() {
  //   const newRows = getNewRows();
  //   return store?.allRowsData.length + newRows.length;
  // }

  function getCurrentRowIndex() {
    if (!store?.gridApi || !currentRowRef.current) return 0;
    const nodeId = currentRowRef.current.uniqueId ?? currentRowRef.current.row_id;
    const node = store.gridApi.getRowNode(nodeId);    
    return node ? node.rowIndex : 0;
  }

  function getTotalRowsCount() {
    return store?.gridApi ? store.gridApi.getDisplayedRowCount() : 0;
  }

  // Reusable key generator to cache formatted values for a column/value pair.
  // const columnFormatKey = (columnId, value, rowId) => {
  //   // value can be complex: stringify safely
  //   try {
  //     // Use JSON.stringify for structured values, fallback to String
  //     return `${columnId}::${rowId}::${typeof value === 'string' ? value : JSON.stringify(value)}`;
  //   } catch (e) {
  //     return `${columnId}::${rowId}::${String(value)}`;
  //   }
  // };

  const formatDateAndTime = useCallback((value, colOptions, columnTypeId) => {
    return `${formatDate(
      value,
      "YYYY-MM-DD",
      colOptions ? colOptions["format"] : (columnTypeId == "4" ? "M j, Y" : "M j, Y g:iA")
    )} ${formatTime(value, columnTypeId)}`;
  }, []);

  const getFormattedCommentsData = useCallback((comments) => {
    let result = '';
    let commentsArray = getFormattedCommentsArray({store}, comments, true) ?? [];
    if (commentsArray.length > 0) result = commentsArray.join('<br>');
    return result;
  }, [store]);

  const convertStringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    let color = "#";
    for (let i = 0; i < 3; i++) {
      let value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).substr(-2);
    }
    return color;
  };

  const getBackGroundColor = (value, param) => {
    if (param?.columnRaw?.name != "Operation") {
      return "grey";
    }
    return convertStringToColor(value);
  };

  const getFormattedDropdownValue = (value, colOptions, columnTypeId) => {
    try {
      const options = (colOptions?.options || []).map((option, index) => ({
        label: typeof option === "string" ? option.trim() : option,
        background: colOptions.colors?.[index] || "grey",
      }));

      let valuesArrToShow = [];

      if (columnTypeId !== "3") {
        const values = value ? value.split(",") : [];
        values.forEach((val) => {
          const found = options.find((option) => option.label === val.trim());
          valuesArrToShow.push(found || { label: val.trim(), background: "grey" });
        });
      } else {
        if (value?.toString().trim() !== "") {
          const trimmedValue = typeof value === "string" ? value.trim() : value;
          const match = options.find((option) => option.label === trimmedValue);
          valuesArrToShow.push(match ? match : { label: trimmedValue, background: "grey" });
        }
      }

      return valuesArrToShow.map((option, idx) => {
        if (option) {
          const trimmedValue = option.label.length > 15 ? option.label.slice(0, 15) + "..." : option.label;

          const color = tinycolor(option.background).getLuminance() > 0.5
            ? tinycolor(option.background).darken(60).toString()
            : COLORS.PRIMARY_COLOR;
          
          const backgroundColor = option
            ? option.background
            : trimmedValue
            ? (store?.listConfig?.list?.is_log_sheet === 1 ? getBackGroundColor(option.label) : "grey")
            : "transparent";

          return (
            <Tooltip 
              key={idx} 
              title={option.label.length > 15 ? option.label : ""} 
              placement="bottom"
              PopperProps={{
                className: 'rowDetailTooltip',
              }}
            >
              <div
                className="dropDownChips"
                style={{
                  color,
                  backgroundColor,
                  margin: '3px',
                  padding: '12px',
                  borderRadius: '20px',
                  textAlign: 'center',
                  display: 'inline-block',
                  alignItems: 'center',
                  height: '26px',
                  maxWidth: '120px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'default',
                  lineHeight: '0px'
                }}
              >
                {trimmedValue}
              </div>
            </Tooltip>
          );
        }
      });  
    } catch (err) {
      console.log('formatting dropdown value error: ', err);
      return "";
    }
  };

  const handleSaveValue = useCallback(
    async (newValue, oldValue, column) => {
      try {
        //colDef, node, api
        // store.rowDetailScrollPos = bodyRef.current?.scrollTop ? bodyRef.current.scrollTop : store.rowDetailScrollPos;

        const rowRef = currentRowRef.current;
        if (!rowRef) {
          console.warn("handleSaveValue: no currentRow available");
          return;
        }
        const targetNode = store?.gridApi?.getRowNode(rowRef?.uniqueId ?? rowRef?.row_id)
        const res = await store?.addEditCell(
          {...params, node: targetNode, colDef: getColumnDefById(column.id)},
          rowRef.row_id,
          column.id,
          newValue,
          column?.column_type_id,
          targetNode?.rowIndex,
          false,
          true,
          column
        );

        if (res.success && res.data) {
          const columnName = editorState.editorColKey ?? column?.name;
          handleEditorState(false);
          window.appStore.notificationStore.showNotification("Value saved successfully!");
          const rowDataBeforeSaving = {...rowRef};
          // normalize for lookup types
          const normalizedNewValue = (column?.column_type_id == '10' || column?.column_type_id == '10-1')
            ? newValue.value
            : newValue;

          // update currentRow raw data
          let updatedRow = { ...rowRef, ...res.data };
          setCurrentRow(updatedRow);
            // ['column_' + column.id]: res.data['column_' + column.id] ?? normalizedNewValue

          // if  (column?.column_type_id == '10' || column?.column_type_id == '10-1') {
          //   // update reference id also
          //   updatedRow = {
          //     ...updatedRow,
          //     ['column_' + column.id + '_reference_id']: res.data['column_' + column.id + '_reference_id']
          //   };
          // }

          // // update grid row for consistency
          // res.data['uniqueId'] = rowRef?.uniqueId ?? res.data['row_id'];
          // const targetNode = store?.gridApi.getRowNode(res.data['uniqueId']);
          // if (targetNode) targetNode.setData({ ...targetNode.data, ...res.data });
          if (targetNode) {
            handleDuplicateCountsUpdation(oldValue, res.data['column_' + column.id], column, rowRef.row_id);
            targetNode.setData({ ...targetNode.data, ...res.data });
          }

          // Update formatted value for this column (reformat and cache)
          const formattedValue = await formatValueByColumnType(
            res.data['column_' + column.id] ?? normalizedNewValue,
            column,
            false,
            updatedRow
          );
          // const columnName = editorState.editorColKey ?? column?.name;

          // ✅ Instead of deleting, update rowCache with the latest formatted data
          const existing = rowCache.get(updatedRow.row_id) || { formattedData, metaData };
          rowCache.set(updatedRow.row_id, {
            formattedData: { ...existing.formattedData, [columnName]: formattedValue },
            metaData: { ...existing.metaData }
          });

          // Bust any column-format entry for that column+oldValue (best-effort)
          // try {
            // const oldKey = columnFormatKey(column.id, rowDataBeforeSaving, res.data?.row_id ?? rowRef?.row_id);
            // columnFormatCache.delete(oldKey);
            // const newKey = columnFormatKey(column.id, res.data[`column_${column.id}`] ?? normalizedNewValue, res.data?.row_id ?? rowRef?.row_id);
            // if (!React.isValidElement(formattedValue)) columnFormatCache.set(newKey, formattedValue);
          // } catch (e) { /* ignore */ }

          // Update UI state
          setFormattedData(prev => ({ ...prev, [columnName]: formattedValue }));
          // data['column_' + column.id] = res.data['column_' + column.id] ?? normalizedNewValue;
          // if  (column?.column_type_id == '10' || column?.column_type_id == '10-1') {
          //   data['column_' + column.id + '_reference_id'] = res.data['column_' + column.id + '_reference_id'];
          // }

          // If this column affects formulas/code snippets, recompute those affected columns
          (async () => {
            const updatedFormulaAndCodeSnippetValues = {};
            for (const columnId of Object.keys(formulaAndCodeSnippetColumnIds)) {
              if (res.data[`column_${columnId}`] !== rowRef[`column_${columnId}`]) {
                const formattedVal = await formatValueByColumnType(
                  res.data[`column_${columnId}`] ?? '',
                  {...formulaAndCodeSnippetColumnIds[columnId], id: columnId},
                  false,
                  res.data
                );
                updatedFormulaAndCodeSnippetValues[formulaAndCodeSnippetColumnIds[columnId]?.name] = formattedVal;
                // const oldKey = columnFormatKey(columnId, rowDataBeforeSaving[`column_${columnId}`], res.data?.row_id ?? rowRef?.row_id);
                // columnFormatCache.delete(oldKey);
                // const key = columnFormatKey(columnId, res.data[`column_${columnId}`] ?? '', res.data?.row_id ?? rowRef?.row_id);
                // if (!React.isValidElement(formattedValue)) columnFormatCache.set(key, formattedVal);
              }
            }
            if (Object.keys(updatedFormulaAndCodeSnippetValues).length > 0) {
              // ✅ also refresh rowCache with updated formula column
              const existingRowCache = rowCache.get(res.data.row_id) || { formattedData, metaData };
              rowCache.set(res.data.row_id, {
                formattedData: { ...existingRowCache.formattedData, ...updatedFormulaAndCodeSnippetValues },
                metaData: { ...existingRowCache.metaData }
              });

              setFormattedData(prev => ({ ...prev, ...updatedFormulaAndCodeSnippetValues }));
              // data = { ...data, ...updatedFormulaAndCodeSnippetValues };
            }
          })();

          // handleEditorState(false);
        } else {
          window.appStore.notificationStore.showNotification(
            res.errorDetails?.hoverMessage ?? "Something went wrong.",
            'error'
          );
        }
      } catch (err) {
        console.error('Error saving value.', err);
      }
    },
    [store, params, editorState, formattedData, metaData] // no currentRow dependency!
  );

  const getFormattedLookupValue = (value, columnType, isSubsheet = false, column) => {
    try {
      if (columnType === "percentage") {
        let decimals = String(value).includes(".") ? String(value).split(".")[1].length : 2;
        return (value * 100).toFixed(decimals - 2) + " %";
      } else if (columnType === "date") {
        return formatDateAndTime(value, false, '4') ?? '';
      } else if (columnType === "datetime") {
        return formatDateAndTime(value, false, '16') ?? '';
      } else if (columnType === "file") {
        return <FileViewerForRowDetailPanel
          value={value}
          rowHeight={36}
          isSubsheet={isSubsheet}
          column={column}
          nodeData={currentRowRef.current}
          isReadOnly={column?.normalizedColumnOptions?.is_read_only ?? false}
          setLoading={setLoading} 
          handleSaveValue={handleSaveValue}
          isLookup={true}
          bodyRef={bodyRef}
          store={store}
        />;
      } else {
        return value;
      }
    } catch (err) {
      console.log('Error formatting lookup value.', err);
      return value;
    }
  };

  const formatSubsheetData = useCallback(async(subsheetRowData, listColumns, subsheetRowDataToView) => {
    try {
      const out = [];
      for (const row of subsheetRowData) {
        const [data, creationRelatedData] = await subsheetRowDataToView(row, listColumns, true);
        data['Id'] = row?.row_id ?? '';
        out.push({ ...data, ...creationRelatedData });
      }
      return out;
    } catch (error) {
      console.log('Error: ', error);
      return '';
    }
  }, []);

  // fetchAndFormatAllSubsheetsData: dedupe via subsheetInFlight and store formatted in subsheetCache
  const fetchAndFormatAllSubsheetsData = useCallback(async (payload, allSubsheetColsInfo, getRowDataToView) => {
    try {
      // payload expected to have parent_row_id, parent_list_id, sub_list_ids (JSON string or array)
      const parentRowId = payload['parent_row_id'];
      const subListIds = typeof payload['sub_list_ids'] === 'string'
        ? JSON.parse(payload['sub_list_ids'])
        : (payload['sub_list_ids'] || []);

      const allSubsheetsData = {}; // raw data for each subsheet list id
      const rowDataOfSubsheets = {};
      const formattedSubsheetsData = {};

      // Try to fetch from API (dedupe in-flight requests)
      // const fetchKey = `${parentRowId}::${payload['parent_list_id'] || ''}::${subListIds.join(',')}`;

      // first try to read raw subsheet data via single API call (getSubsheetData)
      // We'll use subsheetInFlight per subsheet for better granularity — but it's ok to use combined fetchKey to control
      let resp = {};
      if (parentRowId) {
        try {
          resp = await ApiClient.List.getSubsheetData(payload);
        } catch (err) {
          console.error('getSubsheetData error:', err);
          resp = {};
        }
      }

      if (resp && resp.success && resp.data) {
        for (const subListId of subListIds) {
          allSubsheetsData[subListId] = resp.data?.[subListId] ?? [];
        }
      } else if (parentRowId) {
        // fallback to getRowData for parent to fetch embedded subsheet columns
        try {
          const response = await ApiClient.List.getRowData({
            list_id: payload['parent_list_id'] ?? window.appStore.containerStore?.listId,
            row_id: payload['parent_row_id']
          });
          if (response && response.data) {
            for (const subListId of subListIds) {
              const columnId = 'column_' + allSubsheetColsInfo[subListId]['columnId'];
              allSubsheetsData[subListId] = response.data?.[columnId] ?? [];
            }
          } else {
            for (const subListId of subListIds) allSubsheetsData[subListId] = [];
          }
        } catch (err) {
          console.error('getRowData fallback error:', err);
          for (const subListId of subListIds) allSubsheetsData[subListId] = [];
        }
      }

      // Now format each subsheet but reuse subsheetCache for parentRowId+subsId
      for (const subsheetId of subListIds) {
        const columnName = allSubsheetColsInfo[subsheetId]['columnName'];
        const columnId = allSubsheetColsInfo[subsheetId]['columnId'];
        const cacheKey = `${parentRowId}::${subsheetId}`;

        // check subsheetCache first
        let formatted = subsheetCache.get(cacheKey);
        if (!formatted) {
          try {
            const subsheetRowData = allSubsheetsData[subsheetId] ?? [];
            formatted = await formatSubsheetData(subsheetRowData, allSubsheetColsInfo[subsheetId]?.subsheetColumns || [], getRowDataToView);
            subsheetCache.set(cacheKey, formatted);
          } catch (err) {
            console.error('formatting subsheet failed for', cacheKey, err);
            formatted = [];
          }
        }

        formattedSubsheetsData[columnName] = formatted ?? [];
        // update raw data store to be used by editors (so editor sees the raw array)
        rowDataOfSubsheets[`column_${columnId}`] = allSubsheetsData[subsheetId] ?? [];
      }

      // merge raw subsheet arrays into parent dataIncludingSubsheets once (do not cause reformat)
      setDataIncludingSubsheets(prev => ({ ...prev, ...rowDataOfSubsheets }));

      return formattedSubsheetsData;
    } catch (error) {
      console.error('Failed fetching subsheet rows: ', error);
      return {};
    }
  }, [formatSubsheetData]);

  // Format a single cell by column type and memoize result in columnFormatCache
  const formatValueByColumnType = useCallback(async (value, column, isSubsheet = false, rowContext = {}) => {
    try {
      if (!column) return value;
      const colId = column?.name ?? column?.id;
      // const key = columnFormatKey(colId, value, rowContext?.row_id ?? currentRowRef.current?.row_id);
      // const cached = columnFormatCache.get(key);
      // if (cached !== null) return cached;

      // compute formatted value (similar to your previous switch-case)
      let formattedValue = value;
      const columnTypeId = column?.column_type_id;
      const normalizedOptions = column?.normalizedColumnOptions ?? normalizeColOptions(column?.options);

      switch (String(columnTypeId)) {
        case '1':
        case '8':
        case '14':
        case '20':
        case '24':
          formattedValue = renderTextValue({ value, columnTypeId, store: store }, true);
          break;
        case "4":
        case "16":
          formattedValue = formatDateAndTime(value, normalizedOptions, columnTypeId);
          break;
        case '5':
        case '17':
        case '22':
          formattedValue = getFormattedValue(columnTypeId, value, normalizedOptions);
          break;
        case '12':
          formattedValue = getCheckBox({ value, data: rowContext });
          break;
        case '3':
        case '11':
        case '13':
          formattedValue = (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '150px' }}>
              {getFormattedDropdownValue(value, normalizedOptions, columnTypeId)}
            </div>
          );
          break;
        case '9':
          formattedValue = (
            <FileViewerForRowDetailPanel
              value={value}
              rowHeight={36}
              isSubsheet={isSubsheet}
              column={column}
              nodeData={rowContext ?? currentRowRef.current}
              isReadOnly={normalizedOptions?.is_read_only ?? false}
              setLoading={setLoading}
              handleSaveValue={handleSaveValue}
              bodyRef={bodyRef}
              store={store}
            />
          );
          return formattedValue; // return early to avoid caching React element
          // break;
        case '7':
        case '19': {
          const fCol = normalizedOptions?.formula_column;
          if (fCol?.column_type_id === '17' || fCol?.column_type_id === '5') {
            const fOpts = normalizeColOptions(fCol?.options);
            formattedValue = getFormattedValue(fCol?.column_type_id, value, fOpts);
          } else if (fCol?.column_type_id === '4' || fCol?.column_type_id === '16') {
            const fOpts = normalizeColOptions(fCol?.options);
            formattedValue = formatDateAndTime(value, fOpts, fCol?.column_type_id) ?? '';
          } else if (fCol?.column_type_id === '1') {
            formattedValue = renderTextValue({ value, columnTypeId: fCol?.column_type_id, store: store }, true);
          } else {
            formattedValue = value;
          }
          break;
        }
        case '10':
        case '10-1':
          formattedValue = normalizedOptions?.column_type
            ? getFormattedLookupValue(value, normalizedOptions?.column_type, isSubsheet, column)
            : value;
          break;
        default:
          formattedValue = value;
      }

      // if (!React.isValidElement(formattedValue)) {
        // columnFormatCache.set(key, formattedValue);
      // }
      return formattedValue;
    } catch (err) {
      console.error("formatValueByColumnType error:", err);
      return value;
    }
  }, [store, formatDateAndTime, handleSaveValue]);

  // MAIN: getRowDataToView with global rowCache & in-flight dedupe
  const getRowDataToView = useCallback(async (row, cols, isSubsheet = false) => {
    try {
      if (!row || !cols) return [{}, {}];
      const rowId = row?.row_id ?? (row?.rowId ?? '');

      // If isSubsheet (formatting a subsheet row) we should avoid using top-level row cache
      if (!isSubsheet) {
        const cached = rowCache.get(rowId);
        if (cached) {
          return [cached.formattedData ?? {}, cached.metaData ?? {}];
        }
      }

      // Begin building formattedRowData
      let formattedRowData = {};
      let rowCreationAndUpdationData = {};
      let allSubsheetsPayload = {};
      let allSubsheetColsInfo = {};

      // apply hidden fields if needed (same as before)
      if (!isSubsheet && store.checkIfHiddenFieldsExists()) {
        if (store?.hiddenColumnsData && Object.keys(store.hiddenColumnsData).length > 0) {
          row = { ...row, ...store.hiddenColumnsData[row['row_id']] };
        }
      }

      // Iterate columns (this is hot). For each column we first check columnFormatCache for column/value pair.
      for (const column of (cols || [])) {
        // short-circuit for creation/updated/createdBy special columns
        if (column.id === 'created_at') {
          rowCreationAndUpdationData['Created On'] = row[column.id];
          continue;
        }
        if (column.id === 'updated_at') {
          rowCreationAndUpdationData['Updated On'] = row[column.id];
          continue;
        }
        if (column.alias_name === 'createdBy') {
          if (row.hasOwnProperty('column_' + column.id)) {
            rowCreationAndUpdationData['Created By'] =
              (row['column_' + column.id] === 'null' || row['column_' + column.id] === null)
                ? ''
                : renderTextValue({ value: row['column_' + column.id], columnTypeId: '14', store: store }, true);
          }
          continue;
        }

        const value = row['column_' + column.id] ?? '';
        const columnTypeId = column['column_type_id'];
        const normalized = normalizedColumnsById.get(column.id) || column;
        const colOptions = normalized.normalizedColumnOptions ?? normalizeColOptions(normalized?.options);

        let formattedVal = '';

        // If this column is a subsheet (type '6') and has more than 2 list columns, we will either format from local store or queue subsheet fetch
        if (String(columnTypeId) === '6') {
          if (column.list_columns.length > 2 && Number(value) > 0) {
            const subsheetListId = colOptions?.sub_list_id;
            const subStores = window.appStore.containerStores?.[subsheetListId];
            const currStore = subStores?.currentSubsheetStore;
            if (currStore && Object.keys(currStore).length > 0 && currStore?.parentRowId == row?.row_id) {
              let newRows = (currStore.newRows || []).filter(r => r.row_id > 0);
              const subsheetRowData = [...(currStore.allRowsData || []), ...newRows];
              formattedVal = await formatSubsheetData(subsheetRowData, column?.list_columns, getRowDataToView) ?? '';
            } else {
              // queue payload for single batch fetch later
              allSubsheetsPayload['parent_row_id'] = row?.row_id;
              allSubsheetsPayload['parent_list_id'] = column?.list_id;
              if (!allSubsheetsPayload['sub_list_ids']) allSubsheetsPayload['sub_list_ids'] = [];
              allSubsheetsPayload['sub_list_ids'].push(subsheetListId);
              allSubsheetColsInfo[subsheetListId] = {
                columnId: column?.id,
                columnName: column.name,
                subsheetColumns: column?.list_columns
              };
            }
          } else {
            formattedVal = '';
          }
          // done handling subsheet
        } else {
          // For normal columns use formatValueByColumnType which itself caches column+value pair
          formattedVal = await formatValueByColumnType(value, { ...column, normalizedColumnOptions: colOptions }, isSubsheet, row);
        }

        if (column?.alias_name !== 'star') {
          formattedRowData[column.name] = (formattedVal === 'null' || formattedVal === null) ? '' : formattedVal;
        }
      }

      // If we collected subsheet payloads, fetch them once and format
      if (Object.keys(allSubsheetColsInfo).length > 0 && Object.keys(allSubsheetsPayload).length > 0) {
        allSubsheetsPayload['sub_list_ids'] = JSON.stringify(allSubsheetsPayload['sub_list_ids']);
        const formattedSubsheetsData = await fetchAndFormatAllSubsheetsData(allSubsheetsPayload, allSubsheetColsInfo, getRowDataToView);
        formattedRowData = { ...formattedRowData, ...formattedSubsheetsData };
      }

      // comments formatting
      if (!isSubsheet && row?.comments && row?.comments.length > 0) {
        const formattedCommentsData = getFormattedCommentsData(row.comments);
        if (formattedCommentsData !== '') {
          rowCreationAndUpdationData = {
            ...rowCreationAndUpdationData,
            [`Notes/Comments (${row.comments.length})`]: formattedCommentsData
          };
        }
      }

      // Cache top-level row formatting
      if (!isSubsheet) {
        rowCache.set(rowId, { formattedData: formattedRowData, metaData: rowCreationAndUpdationData });
      }

      return [formattedRowData, rowCreationAndUpdationData];
    } catch (error) {
      console.error("Error in getRowDataToView:", error);
      return [{}, {}];
    }
  }, [
    normalizedColumnsById,
    formatValueByColumnType,
    fetchAndFormatAllSubsheetsData,
    formatSubsheetData,
    getFormattedCommentsData,
    store
  ]);

  useEffect(() => {
    // Effect: when currentRow changes, load formatted data from cache or compute once
    let cancelled = false;
    (async () => {
      const rowRef = currentRowRef.current;
      setLoading(true);
      if(dataIncludingSubsheets.row_id !== rowRef.row_id) setDataIncludingSubsheets({ ...rowRef });

      const rowId = rowRef?.row_id;
      // if editor open, still prefer to show cached formatted until save is done
      const cached = rowId ? rowCache.get(rowId) : null;
      if (cached) {
        if (!cancelled) {
          setFormattedData(cached.formattedData ?? {});
          setMetaData(cached.metaData ?? {});
          setLoading(false);
        }
        return;
      }

      // await getRowDataToView (internally dedupes)
      const [mainData, meta] = await getRowDataToView(rowRef, columns, false);
      if (cancelled) return;

      setFormattedData(mainData);
      setMetaData(meta);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [currentRow, columns, getRowDataToView, editorState.open]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleSettings = () => {
    if (!isHeaderSaving) {
      setSettingsOpen((prev) => !prev);
    }
  };

  function handleDuplicateCountsUpdation(oldValue, newValue, column, row_id) {
    try {
      if (['1','8','14','20', '5','17','22','4','16'].includes(String(column?.column_type_id))) {
        const columnOpts = getColumnDefById(column.id);
        if (columnOpts?.cellRendererParams?.columnOptions?.is_highlighted) {
          if (!store.duplicateColumnsData.hasOwnProperty(`column_${column.id}`)) {
            store.duplicateColumnsData[`column_${column.id}`] = {};
          }
          let formattedNewValue = newValue;
          let formattedOldValue = oldValue;
          if (['4', '16'].includes(String(column?.column_type_id))) {
            formattedNewValue = store?.formatDateTimeValue(newValue);
            formattedOldValue = store?.formatDateTimeValue(oldValue);
          }
          store?.modifyDuplicatesDataCount(`column_${column.id}`, formattedNewValue, true, row_id);
          store?.modifyDuplicatesDataCount(`column_${column.id}`, formattedOldValue, false, row_id)
        }
      }
    } catch (error) {
      console.log("Duplicate count updation error from Row view panel. ",error);
    }
  };

  const handleEditClick = (columnName) => {
    handleEditorState(true, currentRowRef.current?.['column_' + columnInfoByName[columnName]?.id], columnName);
  };

  const handleSubsheetUpdation = async (updatedData, column) => {
    const rowRef = currentRowRef.current;
    const columnName = editorState.editorColKey ?? column?.name;
    setDataIncludingSubsheets(prev => ({ ...prev, ['column_' + column.id]: updatedData }));
    const formattedValue = await formatSubsheetData(updatedData, column?.list_columns, getRowDataToView);

    // ✅ Instead of deleting, update rowCache with fresh subsheet value
    const existingRowCache = rowCache.get(rowRef.row_id) || { formattedData, metaData };
    rowCache.set(rowRef.row_id, {
      formattedData: { ...existingRowCache.formattedData, [columnName]: formattedValue },
      metaData: { ...existingRowCache.metaData }
    });

    // Also update subsheetCache for the parentRow::subsheetId
    const subsheetListId = column?.normalizedColumnOptions?.sub_list_id;
    if (subsheetListId) {
      const cacheKey = `${rowRef.row_id}::${subsheetListId}`;
      subsheetCache.set(cacheKey, formattedValue);
    }

    setFormattedData(prev => ({ ...prev, [columnName]: formattedValue }));
    setCurrentRow(prev => ({...prev, ['column_' + column.id]: String(updatedData.length)}));
    currentRowRef.current = { ...currentRowRef.current, ['column_' + column.id]: String(updatedData.length) };
    // data['column_' + column.id] = String(updatedData.length);
  };

  const handleSaveComments = (newComments, oldComments, isUpdated) => {
    if (isUpdated) {
      const rowRef = currentRowRef.current;
      const updated = { ...rowRef, comments: newComments, comments_count: String(newComments.length) };
      setCurrentRow(updated);

      store.comments[rowRef.row_id] = updated.comments_count;
      const rowId = rowRef.uniqueId ?? rowRef['row_id'];
      const targetNode = store?.gridApi.getRowNode(rowId);
      targetNode?.setData({ ...targetNode.data, comments: updated.comments, comments_count: updated.comments_count });
      store.gridApi.refreshCells({
        rowNodes: [targetNode],
        columns: ["userEvents", "commentsId"],
        force: true,
      });

      const oldColumnName = editorState.editorColKey ?? `Notes/Comments (${oldComments.length})`;
      const newColumnName = `Notes/Comments (${updated.comments.length})`;

      const newMeta = { ...metaData };
      delete newMeta[oldColumnName];
      newMeta[newColumnName] = getFormattedCommentsData(newComments);

      setMetaData(newMeta);

      handleEditorState(editorState.open, editorState.editorValue, newColumnName);

      // ✅ Update rowCache instead of deleting it
      const existingRowCache = rowCache.get(rowRef.row_id) || { formattedData, metaData };
      rowCache.set(rowRef.row_id, {
        formattedData: { ...existingRowCache.formattedData },
        metaData: newMeta
      });
    }
    handleEditorState(false);
  };

  const saveAndReplaceHeaderTitle = (value) => {
    setIsHeaderSaving(true);
    if (store?.columnIdsByName && store?.columnIdsByName?.[value]) {
      store.view.options.rowViewHeader = store?.columnIdsByName[value];
    } else {
      store.view.options.rowViewHeader = value;
    }
    store.saveViewOptions();
    setHeaderTitle(getFormattedHeaderName(currentRowRef.current) || `Row ${currentRowRef.current?.row_id}`);
    setSettingsOpen(false);
    setIsHeaderSaving(false);
  };

  // const navigate = useCallback((offset) => {
  //   const newRows = getNewRows();
  //   const rows = [...store?.allRowsData, ...newRows] || [];
  //   const nextIndex = Number(recordIndex) + offset;
  //   if (nextIndex < 0 || nextIndex >= rows.length) return;

  //   const base = rows[nextIndex];
  //   // prefer freshest grid node data if present
  //   const node = store?.gridApi?.getRowNode(base.uniqueId ?? base.row_id);
  //   const nextRow = node?.data || base;

  //   // close any open editor
  //   // setEditorState({...editorState, open: false});
  //   // store.isRowViewEditorOpen = false;

  //   store.rowDetailScrollPos = 0;

  //   if (nextRow) {
  //     // swap row & index
  //     setRecordIndex(nextIndex);
  //     setCurrentRow(nextRow);

  //     // update header immediately (no wait for effect)
  //     setHeaderTitle(getFormattedHeaderName(nextRow) || `Row ${nextRow?.row_id}`);
  //   }
  // }, [recordIndex, getFormattedHeaderName, store]);

  const isValidRow = useCallback((node) => {
    if (!node) return false;    
    if (node.group || node.footer) return false; 
    if (!node.data) return false; 
    if (node.data.row_id === "+" || node.data.uniqueId === "+") return false;
    if (!node.data.row_id || node.data.row_id == "") return false;
    return true;
  }, []);

  const navigate = useCallback((direction) => {
    if (!store?.gridApi) return;
    const totalCount = store.gridApi.getDisplayedRowCount();
    let currentIndex = getCurrentRowIndex();
    let attemptIndex = currentIndex + direction;

    while (attemptIndex >= 0 && attemptIndex < totalCount) {
      const node = store.gridApi.getDisplayedRowAtIndex(attemptIndex);

      if (isValidRow(node)) {
        setLoading(true);
        store.rowDetailScrollPos = 0;
        const nextRow = node.data;
        setCurrentRow(nextRow);
        currentRowRef.current = nextRow;
        setRecordIndex(attemptIndex);
        
        setHeaderTitle(getFormattedHeaderName(nextRow) || `Row ${nextRow?.row_id}`);
        return; 
      }      
      attemptIndex += direction;
    }
  }, [recordIndex, getFormattedHeaderName, store, isValidRow]);

  const hasNext = useMemo(() => {
    if (!store?.gridApi) return false;
    const totalCount = store.gridApi.getDisplayedRowCount();
    let attemptIndex = recordIndex + 1;
    while (attemptIndex < totalCount) {
      const node = store.gridApi.getDisplayedRowAtIndex(attemptIndex);
      if (isValidRow(node)) return true;
      attemptIndex++;
    }
    return false;
  }, [store, recordIndex, isValidRow]);

  const hasPrev = useMemo(() => {
    if (!store?.gridApi) return false;
    let attemptIndex = recordIndex - 1;
    while (attemptIndex >= 0) {
      const node = store.gridApi.getDisplayedRowAtIndex(attemptIndex);
      if (isValidRow(node)) return true;
      attemptIndex--;
    }
    return false;
  }, [store, recordIndex, isValidRow]);

  const handlePrev = useCallback(() => navigate(-1), [navigate]);
  const handleNext = useCallback(() => navigate(1), [navigate]);

  const ExpandableText = ({ text, isSubsheet = false }) => {
    const [expanded, setExpanded] = useState(false);
    const toggle = () => setExpanded(!expanded);
    const trimSize = isExpanded ? 250 : 150;
    let style = (isExpanded && isSubsheet) ? expandedSubsheetStyles.rowCell : (!isExpanded && isSubsheet) ? subsheetStyles.rowCell : {};
    style = {...style, whiteSpace: "pre-wrap", wordBreak: "break-word"};

    if (!text) return '';

    if (text.length <= trimSize) {
      return <div style={style}>{text}</div>;
    }

    return (
      <div style={style}>
        {expanded ? text : `${text.slice(0, trimSize)}... `}
        <button
          onClick={toggle}
          style={{ color: "#4463ff", marginLeft: "4px", cursor: "pointer", textDecoration: "underline", border: "none", backgroundColor: "#ffffff" }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      </div>
    );
  };

  const renderValue = (value, colName) => {
    if (value === null || value === undefined || value === "") return "";


    if (React.isValidElement(value)) {
      return value;
    }

    if (typeof value === "string" && /<\/?[a-z][\s\S]*>/i.test(value)) {
      return (
        <div
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      );
    }
    
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
      const orderedKeys = (obj) => {
        const keys = Object.keys(obj);
        if (keys.includes("Id")) {
          return ["Id", ...keys.filter((k) => k !== "Id" && k !== "Created By"), "Created By"];
        }
        return keys;
      };

      return (
        <TableContainer 
          component={Paper} 
          style={isExpanded ? expandedSubsheetStyles.container : subsheetStyles.container}
        >
          <Table size="small" stickyHeader>
            <TableHead style={{ backgroundColor: "#334155" }}>
              <TableRow>
                {orderedKeys(value[0]).map((key) => (
                  <TableCell key={key} style={subsheetStyles.headerCell} >
                    {key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {value.map((row, idx) => (
                <TableRow key={idx}>
                  {orderedKeys(row).map((key) => (
                    <TableCell key={key} style={subsheetStyles.tableCell}>
                      {
                        React.isValidElement(row[key]) ? (
                          row[key]
                        ) : typeof row[key] === "string" && /<\/?[a-z][\s\S]*>/i.test(row[key]) ? (
                          <div
                            dangerouslySetInnerHTML={{ __html: row[key] }}
                            style={isExpanded ? expandedSubsheetStyles.rowCell : subsheetStyles.rowCell}
                          />
                        ) : (
                          <ExpandableText text={String(row[key])} isSubsheet={true} />
                        )
                      }
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else if (Array.isArray(value) || React.isValidElement(value)) {
      return value;
    } else if (typeof value === "string") {
      return (<ExpandableText text={value} isSubsheet={false} />);
    } else {
      return <span>{value}</span>;
    }
  };


  const getStyles = (valType, isSubsheet) => {
    let style = styles;
    if (valType === 'value' && isSubsheet) {
      return isExpanded ? expandedViewStyles.subsheetValue : styles.subsheetValue;
    } else if (valType === 'value' && !isSubsheet) {
      return isExpanded ? expandedViewStyles.value : styles.value;
    } else {
      style = (isSubsheet && isExpanded) ? expandedSubsheetStyles : 
              (isSubsheet && !isExpanded) ? subsheetStyles :
              (!isSubsheet && isExpanded) ? expandedViewStyles : 
              styles;
    }
    return style[valType];
  };

  const getCellBackgroundColor = (columnName) => {
    const columnId = columnInfoByName[columnName]?.id;
    if (!columnId) return null;
    return currentRowRef.current?.[`column_${columnId}_bg_color`] || null;
  };

  return (
    <>
      <style>{keyframes}</style>

      {
        getEditorByColumnType(
          {
            editorValue: editorState.editorValue,
            editorOpen: editorState.open,
            editorColKey: editorState.editorColKey,
            column: columnInfoByName[editorState.editorColKey],
            params,
            store,
            rowNode: store?.gridApi?.getRowNode(currentRowRef.current?.uniqueId ?? currentRowRef.current?.row_id),
            data: currentRowRef.current,
            dataIncludingSubsheets,
            setEditorOpen: handleEditorState,
            setLoading,
            handleSaveValue,
            handleSubsheetUpdation,
            handleSaveComments,
            bodyRef
          }
        )
      }

      <div style={isExpanded ? expandedViewStyles.overlay : styles.overlay} onClick={onClose}>
        <div style={isExpanded ? expandedViewStyles.backdrop : styles.backdrop}></div>
        <div style={isExpanded ? expandedViewStyles.panel : styles.panel} onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              ...styles.header,
              ...(currentRowRef.current?.row_bg_color ? { backgroundColor: currentRowRef.current.row_bg_color } : {}),
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {isHeaderSaving ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress />
                </Box>
                ) : <h2 style={styles.title}>{headerTitle}</h2>
              }

              {isAdmin && (
                <>
                  <IconButton ref={settingsButtonRef} onClick={toggleSettings} size="small" disabled={isHeaderSaving}>
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                  {settingsOpen && settingsButtonRef.current && !isHeaderSaving && (
                    <Popover
                      open={settingsOpen}
                      anchorEl={settingsButtonRef.current}
                      onClose={() => setSettingsOpen(false)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      style={{ zIndex: 10001 }}
                      PaperProps={{
                        style: {
                          padding: 16,
                          width: 320,
                          maxHeight: 400,
                          overflowY: "auto",
                          borderRadius: 8,
                          boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.1)",
                        },
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                          Pick a Column for Header Name
                        </Typography>
                        <IconButton size="small" onClick={() => setSettingsOpen(false)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Autocomplete
                        freeSolo
                        disableClearable
                        options={options}
                        openOnFocus
                        ListboxProps={{
                          style: {
                            maxHeight: 300,
                            overflowY: "auto",
                            maxWidth: 300,
                            overflowX: "hidden",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          },
                        }}
                        noOptionsText={
                          'Press Enter to set your input as Header Name'
                        }
                        onChange={(e, newValue) => saveAndReplaceHeaderTitle(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Header name"
                            variant="outlined"
                            size="small"
                            autoFocus
                            onFocus={(e) => {
                              e.target.select();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                saveAndReplaceHeaderTitle(e.target.value);
                              }
                            }}
                            helperText={
                            params.inputProps.value !== '' && !options.includes(params.inputProps.value)
                              ? 'Press Enter to set your input as Header Name'
                              : ''
                          }
                          />
                        )}
                        PopperComponent={(props) => (
                          <div {...props} style={{
                            zIndex: 2147483648,
                            backgroundColor: "#fff",
                            boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
                            borderRadius: 4,
                            padding: "4px 0",
                            maxHeight: 300,
                            // maxWidth: 300,
                            overflowY: "auto",
                          }} />
                        )}
                      />
                    </Popover>
                  )}
                </>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Tooltip
                title={"Previous Row"}
                placement="bottom"
                PopperProps={{
                  className: 'rowDetailTooltip',
                }}
              >
                <IconButton
                  onClick={handlePrev}
                  disabled={!hasPrev}
                  style={{ opacity: (!hasPrev) ? 0.25 : 1 }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
              </Tooltip>

              <Tooltip
                title={"Next Row"}
                placement="bottom"
                PopperProps={{
                  className: 'rowDetailTooltip',
                }}
              >
                <IconButton
                  onClick={handleNext}
                  disabled={!hasNext}
                  style={{ opacity: (!hasNext) ? 0.25 : 1 }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </Tooltip>

              <IconButton onClick={toggleExpanded}>
                {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
              <button
                style={styles.closeBtn}
                onMouseOver={(e) => (e.target.style.color = "#000")}
                onMouseOut={(e) => (e.target.style.color = "#666")}
                onClick={onClose}
              >
                ✕
              </button>
            </div>
          </div>

          <div ref={bodyRef} key="row-detail-body" style={{ ...styles.body, overflow: "auto" }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
              </Box>
              ) : 
              Object.entries({ ...formattedData, ...metaData }).map(([key, value]) => (
                <div
                  className="rowCellInfo_RowDetailPanel"
                  key={key} 
                  style={{
                    ...styles.row,
                    ...(getCellBackgroundColor(key) ? { backgroundColor: getCellBackgroundColor(key) } : {}),
                  }}
                >
                  <div style={isExpanded ? expandedViewStyles.key : styles.key}>{key}</div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, width: "100%" }}>
                    <div style={getStyles('value', Array.isArray(value))}>{renderValue(value, key)}</div>
                    {(checkEditability(columnInfoByName[key], currentRowRef.current) || key === `Notes/Comments (${currentRowRef.current?.comments?.length})`) && (
                      <IconButton
                        className="cellEditAndAddIcon_RowDetailPanel"
                        size="small"
                        onClick={() => handleEditClick(key)}
                        style={styles.cellEditIcon}
                      >
                        {columnInfoByName[key]?.column_type_id == '9' ? 
                          (<AddIcon fontSize="small" style={{ color: "rgba(0, 0, 0, 0.54)", cursor: 'pointer' }} />) :
                          (<EditIcon fontSize="small" style={{ color: "rgba(0, 0, 0, 0.54)", cursor: 'pointer' }} />)
                        }
                      </IconButton>
                    )}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </>
  );
};

export default RowDetailPanel;
